import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Copy, ExternalLink, Send, Loader2, CheckCircle2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

interface EmailHubDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EmailHubDialog({ isOpen, onClose }: EmailHubDialogProps) {
  const [view, setView] = useState<"options" | "form">("options");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const emailAddress = "hello@acceleratebyte.com";

  const handleCopy = () => {
    navigator.clipboard.writeText(emailAddress);
    toast.success("Email address copied to clipboard!");
  };

  const handleOpenApp = () => {
    window.location.href = `mailto:${emailAddress}?subject=Inquiry%20from%20Website`;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !message) {
      toast.error("Please fill in your email and message");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Website User",
          email,
          company: "Direct Message",
          service: "General Inquiry",
          message,
        }),
      });

      if (!res.ok) throw new Error("Failed to send message");

      setIsSuccess(true);
      toast.success("Message sent! We'll get back to you shortly.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setView("options");
    setIsSuccess(false);
    setEmail("");
    setMessage("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px] bg-card border-border/50 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold flex items-center gap-3">
            <Mail className="h-6 w-6 text-primary" />
            Reach Out to Us
          </DialogTitle>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-8 flex flex-col items-center text-center">
            <CheckCircle2 className="h-12 w-12 text-primary mb-4" />
            <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
            <p className="text-sm text-muted-foreground mb-6">We'll get back to you at {email} within 24 hours.</p>
            <Button onClick={reset} className="w-full">Close</Button>
          </div>
        ) : view === "options" ? (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground mb-6 text-center">
              Choose the easiest way for you to contact our team.
            </p>
            
            <button 
              onClick={() => setView("form")}
              className="w-full group flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-muted/20 hover:bg-primary/5 hover:border-primary/50 transition-all text-left"
            >
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                <MessageSquare className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold">Write a Quick Message</p>
                <p className="text-xs text-muted-foreground">Most reliable - send right from here</p>
              </div>
            </button>

            <button 
              onClick={handleOpenApp}
              className="w-full group flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-muted/20 hover:bg-blue-500/5 hover:border-blue-500/50 transition-all text-left"
            >
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform">
                <ExternalLink className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold">Open My Mail App</p>
                <p className="text-xs text-muted-foreground">For Outlook, Apple Mail, or Mobile app</p>
              </div>
            </button>

            <button 
              onClick={handleCopy}
              className="w-full group flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-muted/20 hover:bg-purple-500/5 hover:border-purple-500/50 transition-all text-left"
            >
              <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center text-purple-500 group-hover:scale-110 transition-transform">
                <Copy className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold">Copy Email Address</p>
                <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{emailAddress}</p>
              </div>
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="userEmail">Your Email</Label>
              <Input 
                id="userEmail" 
                type="email" 
                placeholder="you@example.com" 
                value={email}
                onChange={e => setEmail(e.target.value)}
                required 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="userMessage">How can we help?</Label>
              <Textarea 
                id="userMessage" 
                placeholder="I'd like to discuss a project..." 
                className="h-32 resize-none"
                value={message}
                onChange={e => setMessage(e.target.value)}
                required
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" type="button" onClick={() => setView("options")} className="flex-1">Back</Button>
              <Button type="submit" disabled={isSubmitting} className="flex-[2]">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Send Message</>}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
